import React, { useEffect, useRef, useState } from "react";
import { DiamondNetworkMark } from "./Logo";

const KPIS = [
  { value: 60000, suffix: "", label: "Logements", sub: "d'ici 2035" },
  { value: 8, suffix: "", label: "Pôles urbains", sub: "en Côte d'Ivoire" },
  { value: 300000, suffix: "", label: "Personnes logées", sub: "300 000 vies transformées" },
  { value: 47000, suffix: "", label: "Emplois créés", sub: "directs et indirects" },
];

const PHASES = [
  { label: "2026", note: "Amorçage" },
  { label: "2028", note: "Angré & Bouaké" },
  { label: "2030", note: "Sassandra" },
  { label: "2032", note: "Nord & Ouest" },
  { label: "2035", note: "60 000 unités" },
];

const useCountUp = (target, active, duration = 2200) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else setN(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return n;
};

const formatNumber = (n) => n.toLocaleString("fr-FR").replace(/,/g, "\u00A0").replace(/\s/g, "\u00A0");

const Counter = ({ item, active }) => {
  const n = useCountUp(item.value, active);
  return (
    <div data-testid={`vision-kpi-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="font-heading font-black text-white text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl leading-none tracking-tighter whitespace-nowrap">
        {formatNumber(n)}
        <span className="text-[#E8611A]">{item.suffix}</span>
      </div>
      <div className="mt-4 font-heading font-bold text-white text-lg md:text-xl tracking-tight">
        {item.label}
      </div>
      <div className="mt-1 font-body text-white/50 text-xs md:text-sm">
        {item.sub}
      </div>
    </div>
  );
};

const Vision2035 = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setProgress(100), 400);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section
      id="vision"
      ref={ref}
      data-testid="vision-section"
      className="relative bg-[#0A1C33] text-white py-28 md:py-40 overflow-hidden"
    >
      {/* Diamond motif decor */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(135deg, transparent 46%, #E8611A 46%, #E8611A 54%, transparent 54%)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute -top-16 -right-16 opacity-10">
        <DiamondNetworkMark size={400} tone="white" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-white/60">
            Vision 2035 · En chiffres
          </span>
        </div>

        <h2 className="font-heading font-black text-white text-4xl md:text-6xl leading-[0.95] tracking-tighter max-w-4xl mb-6">
          Le plus ambitieux programme
          <br />
          <span className="text-[#E8611A]">immobilier privé</span> d'Afrique de l'Ouest.
        </h2>
        <p className="font-body text-white/70 text-base md:text-lg max-w-2xl mb-20">
          Un déploiement structuré sur dix ans, aligné avec le PND 2026-2030 de
          la Côte d'Ivoire et le Code des investissements 2024.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16 lg:gap-x-14 border-t border-white/12 pt-16">
          {KPIS.map((k) => (
            <Counter key={k.label} item={k} active={active} />
          ))}
        </div>

        {/* Deployment timeline */}
        <div className="mt-24 md:mt-32" data-testid="vision-timeline">
          <div className="flex items-center justify-between mb-6">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/60">
              Déploiement 2026 → 2035
            </div>
            <div className="hidden md:flex items-center gap-2 text-white/60">
              <span className="font-body text-[0.65rem] uppercase tracking-[0.28em]">42% social</span>
              <span className="kidh-diamond" />
              <span className="font-body text-[0.65rem] uppercase tracking-[0.28em]">1 031 ha Sassandra</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative h-[3px] bg-white/12 w-full">
            <div
              className="absolute inset-y-0 left-0 bg-[#E8611A] transition-all duration-[2200ms] ease-out"
              style={{ width: `${progress}%` }}
            />
            {PHASES.map((p, i) => (
              <div
                key={p.label}
                className="absolute -top-2"
                style={{ left: `${(i / (PHASES.length - 1)) * 100}%`, transform: "translateX(-50%)" }}
              >
                <div
                  className="w-[9px] h-[9px] rotate-45 bg-[#E8611A]"
                  style={{
                    opacity: active ? 1 : 0,
                    transition: `opacity 0.4s ease ${400 + i * 220}ms`,
                  }}
                />
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-5 gap-2">
            {PHASES.map((p) => (
              <div key={p.label} className="text-center md:text-left">
                <div className="font-heading font-bold text-white text-sm md:text-base">
                  {p.label}
                </div>
                <div className="font-body text-white/50 text-[0.65rem] md:text-xs mt-1">
                  {p.note}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Vision2035;
